import * as THREE from "three";
import { isModuleToModuleConnection } from "../../utils/geometry.js";
import { OPPOSITE_SIDES } from "../../utils/constants.js";

// Slack (m) when matching a saved AP offset back to its attachment point. The
// compressed layout format rounds offsets to mm (POS_DECIMALS in layoutCodec.js),
// and no two APs on one face are authored anywhere near this close together.
const AP_OFFSET_MATCH_EPS = 0.005;

/**
 * Pick the AP a saved child was really on, by its authored offset. Returns null
 * for legacy payloads with no saved offset and when nothing matches, leaving the
 * caller's first-free fallback in charge.
 */
export function findAttachmentPointByOffset(candidates, offset) {
  if (!offset) return null;
  return candidates.find(ap => {
    const p = ap.originalLocalPosition;
    return p &&
      Math.abs(p.x - offset.x) < AP_OFFSET_MATCH_EPS &&
      Math.abs(p.y - offset.y) < AP_OFFSET_MATCH_EPS &&
      Math.abs(p.z - offset.z) < AP_OFFSET_MATCH_EPS;
  }) ?? null;
}

export class LayoutSerializer {
  /**
   * Serialize furniture layout to minimal format
   * @returns {Array} Array of serialized furniture objects
   */
  serializeLayout() {
    const serialized = [];

    this.furniture.forEach((furnitureElement) => {
      try {
        const mesh = furnitureElement.getMesh();
        const position = mesh.position;
        // Persist the LOGICAL Y (without the leg-height body lift). applyLegHeight
        // re-applies the lift on restore; otherwise the lift would compound each
        // save/load cycle.
        const logicalY = furnitureElement.getLogicalY();
        // Use 'YXZ' Euler order to correctly extract Y rotation — mesh.rotation.y
        // returns ~0 for 180° due to XYZ gimbal lock (stored as x=π, y≈0, z=π).
        const rotation = new THREE.Euler().setFromQuaternion(mesh.quaternion, 'YXZ').y;

        // Determine parent info if attached
        let attachedTo = null;
        let attachedSide = null;
        let attachedPointOffset = null;
        let attachmentLocalPosition = null;
        if (furnitureElement.attachedTo) {
          attachedTo = furnitureElement.attachedTo.parentFurniture.id;
          attachedSide = furnitureElement.attachedTo.side;
          // Save the original offset of the specific AP used so restore can identify it
          // unambiguously even when multiple APs share the same side (e.g. cascais-l has
          // three "left" module APs at different z-offsets).
          const origPos = furnitureElement.attachedTo.originalLocalPosition;
          if (origPos) {
            attachedPointOffset = { x: origPos.x, y: origPos.y, z: origPos.z };
          }
          // Save where on the attachment point it sits (for sliding components)
          const slide = furnitureElement.attachedTo.slideOf(furnitureElement);
          if (slide) {
            attachmentLocalPosition = { x: slide.x, y: slide.y, z: slide.z };
          }
        }

        const item = {
          id: furnitureElement.id,
          urlCode: furnitureElement.furnitureData.urlCode,
          // Catalog item id — unique even when multiple items share a urlCode
          // (e.g. diagon-bp "Big Pillow" vs diagon-bp-l "Big Pillow L", which differ
          // only by mirror). spawnFurniture strips the catalog `id` (the element's own
          // `id` is a runtime UUID) and re-exposes it as `catalogId`. Lets restore
          // disambiguate same-urlCode variants.
          dataId: furnitureElement.furnitureData.catalogId,
          fabricId: furnitureElement.appliedFabric || furnitureElement.fabricId || null,
          position: { x: position.x, y: logicalY, z: position.z },
          rotation: rotation,
          attachedTo: attachedTo,
          attachedSide: attachedSide,
          attachedPointOffset: attachedPointOffset,
          attachmentLocalPosition: attachmentLocalPosition,
          componentType: furnitureElement.furnitureData.componentType || furnitureElement.type,
          moduleConnectionSide: furnitureElement.moduleConnectionSide || null,
          variantIndex: furnitureElement.variantIndex || 0,
          // Per-element wood finish for items with a dedicated wood surface (oiko-sta).
          woodType: furnitureElement.woodType || null,
        };

        serialized.push(item);
      } catch (error) {
        console.error(`Failed to serialize furniture ${furnitureElement.id}:`, error);
      }
    });

    return serialized;
  }

  /**
   * Load furniture layout from serialized data
   * @param {Array} layoutData - Array of serialized furniture objects
   * @returns {Boolean} Success status
   */
  async loadLayout(layoutData) {
    if (!layoutData || !Array.isArray(layoutData)) {
      console.warn('Invalid layout data');
      return false;
    }

    // Ensure furniture collections are loaded
    if (!this.furnitureCollections || Object.keys(this.furnitureCollections).length === 0) {
      console.error('Furniture collections not loaded. Cannot load layout.');
      return false;
    }

    try {
      this._loadingLayout = true;

      // Clear existing furniture first
      this.clearAll();

      // The catalog is the authority on componentType, not the payload: a rename (`accessory`
      // → `subcushion`) would otherwise strand every share link, stored session and
      // pre-compressed preset on a name that no longer matches any attachment point.
      for (const item of layoutData) {
        const catalogType = this.findCatalogItem(item)?.componentType;
        if (catalogType && catalogType !== item.componentType) {
          item.componentType = catalogType;
        }
      }

      // Build ID to furniture data map for quick lookup
      const idToData = {};

      // Check for duplicate IDs in the layout data
      const ids = layoutData.map(item => item.id);
      const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
      if (duplicateIds.length > 0) {
        console.error('⚠️ DUPLICATE IDs DETECTED in saved layout:', [...new Set(duplicateIds)]);
        console.error('This layout was saved with an older version. Please recreate your layout for proper saving.');
      }

      // PASS 1: Create all root furniture (no attachedTo)
      const rootItems = layoutData.filter(item => !item.attachedTo);
      for (const item of rootItems) {
        const furnitureElement = await this.createFurnitureFromItem(item);
        if (furnitureElement) {
          idToData[item.id] = furnitureElement;
        }
      }

      // PASS 2: Create attached furniture in dependency order
      const attachedItems = layoutData.filter(item => item.attachedTo);

      // Sort by dependency depth (simple approach: try multiple passes)
      let maxPasses = 5;
      let passCount = 0;
      const pendingItems = [...attachedItems];

      while (pendingItems.length > 0 && passCount < maxPasses) {
        passCount++;
        const remainingItems = [];

        for (const item of pendingItems) {
          // Check if parent exists
          const parent = idToData[item.attachedTo];
          if (parent) {
            const furnitureElement = await this.createFurnitureFromItem(item);
            if (furnitureElement) {
              idToData[item.id] = furnitureElement;
            }
          } else {
            // Parent not created yet, try again in next pass
            remainingItems.push(item);
          }
        }

        pendingItems.length = 0;
        pendingItems.push(...remainingItems);

        // If no progress was made, break to avoid infinite loop
        if (remainingItems.length === attachedItems.length) {
          console.warn(`Could not create ${remainingItems.length} furniture items due to missing parents`);
          break;
        }
      }

      // PASS 3: Reconstruct attachments
      for (const item of attachedItems) {
        if (item.attachedTo && item.attachedSide) {
          const childElement = idToData[item.id];
          const parentElement = idToData[item.attachedTo];

          if (childElement && parentElement) {
            // Find attachment point on parent. `attachedPointOffset` is saved for
            // exactly this — a side can carry several APs and they are not
            // interchangeable: bard's four corner pillow slots are all side "top"
            // and differ only by which two faces each one gates on
            // (requiresAdjacentModuleOnSides). Taking the first free one bound the
            // pillow to the opposite corner's slot, and since the saved
            // attachmentLocalPosition is stamped onto it below the scene still looked
            // right — until the next drag ran removeUnsupportedFragileCushions,
            // evaluated the wrong corner's gate and deleted the pillow.
            const candidates = parentElement.attachmentPoints.filter(
              ap => ap.side === item.attachedSide &&
                    !ap.isOccupied &&
                    ap.acceptsComponentTypes.includes(item.componentType)
            );
            const attachmentPoint =
              findAttachmentPointByOffset(candidates, item.attachedPointOffset) ?? candidates[0];

            if (attachmentPoint) {
              const isModuleConnection = isModuleToModuleConnection(childElement, parentElement);

              // For components: Set world rotation BEFORE attaching (like normal spawn flow)
              // For modules: Position and rotation already set, just need to attach
              if (!isModuleConnection) {
                // Component: Need to set world rotation before attachTo converts to local space
                if (item.attachedSide === "inherit") {
                  // Inherit: Match parent's world rotation
                  const attachmentOrientation = attachmentPoint.getAttachmentOrientation();
                  childElement.getMesh().quaternion.copy(attachmentOrientation);
                } else {
                  // Other attachment sides: Get attachment orientation and apply saved rotation offset
                  const attachmentOrientation = attachmentPoint.getAttachmentOrientation();
                  childElement.getMesh().quaternion.copy(attachmentOrientation);

                  // The saved rotation is local rotation that was applied after attachment
                  // We'll apply it after attachTo converts to local space
                }
              }

              // Attach to the attachment point, passing moduleConnectionSide so
              // hasAdjacentModuleOnSide (Case 1) hides the correct panel AP even for
              // rotated modules (where child side != OPPOSITE_SIDES[parent side]).
              // For legacy saves that lack moduleConnectionSide, fall back to
              // OPPOSITE_SIDES[attachedSide] so all APs on the connection face are
              // correctly marked occupied (group fix — prevents snap to a connected face).
              const childConnectionSideForLoad = item.moduleConnectionSide || (isModuleConnection ? OPPOSITE_SIDES[item.attachedSide] : null);
              childElement.attachTo(attachmentPoint, childConnectionSideForLoad);

              if (isModuleConnection) {
                // Module-to-module: Set world rotation (stays in world space)
                childElement.getMesh().rotation.y = item.rotation;
              } else {
                // Component: After attachTo converted to local space, apply any additional local rotation.
                // Use setFromAxisAngle (not rotation.y) to avoid XYZ gimbal lock corruption —
                // after pre-set+attachTo a right panel's quat is {x=π, y≈0, z=π} in XYZ order,
                // so assigning rotation.y=π would corrupt it to identity.
                //
                // Use an epsilon threshold instead of !== 0 to guard against old-format layouts
                // where right panels were serialized as sin(π)≈1.2e-16 (mesh.rotation.y gimbal lock)
                // instead of π. Without the epsilon, setFromAxisAngle(Y, 1.2e-16) ≈ identity,
                // which is completely wrong. Values below 1e-10 are treated as zero so the
                // pre-set+attachTo path (which already gives rotation(π)) is used instead.
                if (item.attachedSide !== "inherit" && Math.abs(item.rotation) > 1e-10) {
                  childElement.getMesh().quaternion.setFromAxisAngle(
                    new THREE.Vector3(0, 1, 0),
                    item.rotation
                  );
                }
              }

              // Restore local position for component connections (panels, backrests, cushions).
              // Use item.position (the saved mesh local position) rather than
              // attachmentLocalPosition, because item.position correctly includes any
              // connectionOffset that was applied during placement.
              if (!isModuleConnection) {
                childElement.getMesh().position.set(
                  item.position.x,
                  item.position.y,
                  item.position.z
                );
              }

              // Restore where on the AP it sits (drives getWorldPosition / sliding)
              if (item.attachmentLocalPosition && childElement.attachedTo) {
                childElement.attachedTo.setSlide(childElement, item.attachmentLocalPosition);
              }

              // Recalculate bounding box after all position/rotation corrections
              childElement.calculateBoundingBox();
            } else {
              console.warn(`Cannot attach ${item.urlCode} - attachment point "${item.attachedSide}" is occupied or unavailable on parent ${parentElement.furnitureData?.urlCode || 'unknown'}`);
            }
          }
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to load furniture layout:', error);
      return false;
    } finally {
      this._loadingLayout = false;
      this.invalidateAttachmentMarkers();
    }
  }

  /**
   * Helper: Create furniture element from serialized item
   * @param {Object} item - Serialized furniture item
   * @returns {FurnitureElement|null} Created furniture element
   */
  async createFurnitureFromItem(item) {
    try {
      const furnitureData = this.findCatalogItem(item);

      if (!furnitureData) {
        console.warn(`Furniture with urlCode ${item.urlCode} not found in collections`);
        return null;
      }

      // Spawn furniture at saved position
      const position = new THREE.Vector3(item.position.x, item.position.y, item.position.z);
      const furnitureElement = await this.spawnFurniture(furnitureData, position);

      if (!furnitureElement) {
        return null;
      }

      // Set the saved ID to maintain parent-child references
      furnitureElement.id = item.id;

      const variants = furnitureElement.furnitureData?.properties?.variants;
      const variantIndex = item.variantIndex ?? 0;
      if (variantIndex > 0 && variants && variantIndex < variants.length) {
        furnitureElement.switchVariant(variantIndex);
      }

      // Modules take their world yaw now, before any link (attachTo reads footprints); components' local yaw waits for PASS 3.
      if (!item.attachedTo || item.componentType === 'module') {
        furnitureElement.getMesh().rotation.y = item.rotation;
        furnitureElement.calculateBoundingBox();
      }

      // Apply material if specified (no propagation during load)
      if (item.fabricId && this.app.getMaterialManager()) {
        await this.applyMaterialToFurniture(furnitureElement, item.fabricId, false);
      }

      // Restore per-element wood finish (dedicated wood surfaces, e.g. oiko-sta).
      if (item.woodType) {
        this.setElementWoodType(furnitureElement, item.woodType);
      }

      return furnitureElement;
    } catch (error) {
      console.error(`Failed to create furniture from item:`, error);
      return null;
    }
  }
}
